import React, { useState } from 'react';
import { useCreateTenant, useUpdateTenant } from '../hooks/useTenants';
import { useEntitlements, useChangeTier } from '../hooks/useEntitlements';
import type { CreateTenant, Tenant, UpdateTenant, Tier } from '@policy-tracker/types';

interface TenantFormProps {
    tenant?: Tenant;
    onSuccess?: () => void;
    onCancel?: () => void;
}

export default function TenantForm({ tenant, onSuccess, onCancel }: TenantFormProps) {
    const isEditing = !!tenant;

    const [formData, setFormData] = useState({
        name: tenant?.name || '',
        subDomain: tenant?.subDomain || '',
        awsAccount: tenant?.awsAccount || '',
        region: tenant?.region || 'us-west-2',
        initialUserEmail: '',
        initialUserFirstName: '',
        initialUserLastName: '',
        tier: 'free' as Tier,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    const createTenant = useCreateTenant();
    const updateTenant = useUpdateTenant();
    const changeTier = useChangeTier();

    const { data: entitlements, isLoading: entitlementsLoading } = useEntitlements(tenant?.tenantId, isEditing);

    // Sync tier from entitlements once loaded
    const [tierSynced, setTierSynced] = useState(false);
    if (isEditing && entitlements && !tierSynced) {
        setFormData((prev) => ({ ...prev, tier: entitlements.tier }));
        setTierSynced(true);
    }

    const mutation = isEditing ? updateTenant : createTenant;

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.subDomain.trim()) {
            newErrors.subDomain = 'Sub domain is required';
        } else if (!/^[a-z0-9-]+$/.test(formData.subDomain)) {
            newErrors.subDomain = 'Sub domain must contain only lowercase letters, numbers, and hyphens';
        }

        if (!formData.awsAccount.trim()) {
            newErrors.awsAccount = 'AWS Account is required';
        } else if (!/^\d{12}$/.test(formData.awsAccount)) {
            newErrors.awsAccount = 'AWS Account must be a 12-digit number';
        }

        if (!formData.region.trim()) {
            newErrors.region = 'Region is required';
        }

        if (!isEditing) {
            if (!formData.initialUserFirstName.trim()) {
                newErrors.initialUserFirstName = 'First name is required';
            }

            if (!formData.initialUserLastName.trim()) {
                newErrors.initialUserLastName = 'Last name is required';
            }

            if (!formData.initialUserEmail.trim()) {
                newErrors.initialUserEmail = 'Email is required';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.initialUserEmail)) {
                newErrors.initialUserEmail = 'Email must be a valid email address';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            if (isEditing) {
                const updateData: UpdateTenant = {};
                if (formData.name !== tenant.name) updateData.name = formData.name;

                if (Object.keys(updateData).length > 0) {
                    await updateTenant.mutateAsync({ id: tenant.tenantId, tenant: updateData });
                }
            } else {
                const createData: CreateTenant = {
                    name: formData.name,
                    subDomain: formData.subDomain,
                    awsAccount: formData.awsAccount,
                    region: formData.region,
                    initialUser: {
                        email: formData.initialUserEmail,
                        firstName: formData.initialUserFirstName,
                        lastName: formData.initialUserLastName,
                    },
                    tier: formData.tier,
                };
                await createTenant.mutateAsync(createData);
            }

            onSuccess?.();
        } catch (error) {
            setErrors({ submit: 'Failed to save tenant. Please try again.' });
        }
    };

    const handleChange = (field: keyof typeof formData) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setFormData((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: '',
            }));
        }
    };

    const handleTierChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newTier = e.target.value as Tier;

        if (!isEditing) {
            // In create mode, just update state directly
            setFormData((prev) => ({ ...prev, tier: newTier }));
            return;
        }

        // In edit mode, show confirmation and call the entitlements API immediately
        const confirmed = window.confirm(`Are you sure you want to change the tier to "${newTier}"? This will take effect immediately.`);

        if (!confirmed) {
            // Revert — re-read the current value from state (don't update)
            return;
        }

        try {
            await changeTier.mutateAsync({ tenantId: tenant!.tenantId, tier: newTier });
            setFormData((prev) => ({ ...prev, tier: newTier }));
        } catch (error) {
            setErrors((prev) => ({ ...prev, tier: 'Failed to change tier. Please try again.' }));
        }
    };

    return (
        <form onSubmit={handleSubmit} aria-label={isEditing ? 'Edit Tenant' : 'Create Tenant'}>
            <div className="form-group">
                <label htmlFor="name" className="form-label">
                    Company Name
                </label>
                <input type="text" id="name" className="form-input" value={formData.name} onChange={handleChange('name')} placeholder="Enter company name" />
                {errors.name && <div className="error">{errors.name}</div>}
            </div>

            {!isEditing && (
                <>
                    <div className="form-group">
                        <label htmlFor="firstName" className="form-label">
                            First Name
                        </label>
                        <input
                            type="text"
                            id="firstName"
                            className="form-input"
                            value={formData.initialUserFirstName}
                            onChange={handleChange('initialUserFirstName')}
                            placeholder="Enter your first name"
                        />
                        {errors.initialUserFirstName && <div className="error">{errors.initialUserFirstName}</div>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="lastName" className="form-label">
                            Last Name
                        </label>
                        <input
                            type="text"
                            id="lastName"
                            className="form-input"
                            value={formData.initialUserLastName}
                            onChange={handleChange('initialUserLastName')}
                            placeholder="Enter your last name"
                        />
                        {errors.initialUserLastName && <div className="error">{errors.initialUserLastName}</div>}
                    </div>
                    <div className="form-group">
                        <label htmlFor="email" className="form-label">
                            Email
                        </label>
                        <input
                            type="text"
                            id="email"
                            className="form-input"
                            value={formData.initialUserEmail}
                            onChange={handleChange('initialUserEmail')}
                            placeholder="Enter your email"
                        />
                        {errors.initialUserEmail && <div className="error">{errors.initialUserEmail}</div>}
                    </div>
                </>
            )}

            <div className="form-group">
                <label htmlFor="subDomain" className="form-label">
                    Sub Domain (this will be how you access the application)
                </label>
                <input
                    type="text"
                    id="subDomain"
                    className="form-input"
                    value={formData.subDomain}
                    onChange={handleChange('subDomain')}
                    placeholder="Enter sub domain (e.g., mycompany)"
                    disabled={isEditing} // Sub domain cannot be changed after creation
                />
                {errors.subDomain && <div className="error">{errors.subDomain}</div>}
                {isEditing && <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '0.25rem' }}>Sub domain cannot be changed after creation</div>}
            </div>

            <div className="form-group">
                <label htmlFor="awsAccount" className="form-label">
                    AWS Account
                </label>
                <input
                    type="text"
                    id="awsAccount"
                    className="form-input"
                    value={formData.awsAccount}
                    onChange={handleChange('awsAccount')}
                    placeholder="Enter 12-digit AWS account ID"
                    disabled={isEditing}
                />
                {errors.awsAccount && <div className="error">{errors.awsAccount}</div>}
                {isEditing && <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '0.25rem' }}>AWS Account cannot be changed after creation</div>}
            </div>

            <div className="form-group">
                <label htmlFor="region" className="form-label">
                    Region
                </label>
                <select id="region" className="form-input" value={formData.region} onChange={handleChange('region')} disabled={isEditing}>
                    {/* TODO: get these from the environment */}
                    <option value="us-east-2">US East (Ohio)</option>
                    <option value="us-west-2">US West (Oregon)</option>
                </select>
                {errors.region && <div className="error">{errors.region}</div>}
            </div>

            <div className="form-group">
                <label htmlFor="tier" className="form-label">
                    Tier
                </label>
                {isEditing && entitlementsLoading ? (
                    <div>Loading tier...</div>
                ) : (
                    <select id="tier" className="form-input" value={formData.tier} onChange={handleTierChange}>
                        <option value="free">Free</option>
                        <option value="standard">Standard</option>
                        <option value="premium">Premium</option>
                    </select>
                )}
                {errors.tier && <div className="error">{errors.tier}</div>}
            </div>

            {errors.submit && (
                <div className="error" style={{ marginBottom: '1rem' }}>
                    {errors.submit}
                </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="submit" className="btn" disabled={mutation.isPending}>
                    {mutation.isPending ? 'Saving...' : isEditing ? 'Update Tenant' : 'Create Tenant'}
                </button>

                {onCancel && (
                    <button type="button" className="btn btn-outline" onClick={onCancel} disabled={mutation.isPending}>
                        Cancel
                    </button>
                )}
            </div>
        </form>
    );
}
